import type {
  TextComposerChannelTransforms,
  TextComposerMmsPayload,
  TextComposerRcsPayload,
  TextComposerSmsPayload,
} from "./channelTransforms";
import {
  getDefaultAuthoringDraftSchema,
  normalizeAuthoringDraft,
} from "../editor/authoringState";
import type { TextComposerAuthoringState } from "../editor/authoringState";
import type { TextComposerExtension } from "../editor/extensionContract";
import {
  collectContentContributions,
  composeNormalize,
} from "../editor/extensionRegistry";
import { defaultExtensions } from "../extensions/defaultExtensions";

export type TextComposerPrimaryChannel = "RCS" | "SMS" | "MMS" | "INVALID";
export type TextComposerFallbackChannel = "SMS" | "MMS" | null;

export interface TextComposerDerivedOutput {
  primaryChannel: TextComposerPrimaryChannel;
  fallbackChannel: TextComposerFallbackChannel;
}

export type TextComposerChannelTransformValue =
  | TextComposerRcsPayload
  | TextComposerSmsPayload
  | TextComposerMmsPayload
  | null;

/**
 * Determines channel routing from content-type flags.
 *   - text only → RCS primary, SMS fallback
 *   - media only → MMS primary, no fallback
 *   - text + media → RCS primary, MMS fallback
 */
export function resolveChannels(
  hasText: boolean,
  hasMedia: boolean,
): TextComposerDerivedOutput {
  if (hasMedia && hasText) {
    return { primaryChannel: "RCS", fallbackChannel: "MMS" };
  }
  if (hasMedia) {
    return { primaryChannel: "MMS", fallbackChannel: null };
  }
  return { primaryChannel: "RCS", fallbackChannel: "SMS" };
}

interface ContentFlags {
  hasText: boolean;
  hasMedia: boolean;
}

function getContentFlags(
  state: TextComposerAuthoringState,
  extensions?: TextComposerExtension[],
): ContentFlags | null {
  const parsedState = getDefaultAuthoringDraftSchema().safeParse(state);
  if (!parsedState.success) {
    return null;
  }

  const exts = extensions ?? defaultExtensions;
  const normalizedState = normalizeAuthoringDraft(parsedState.data);
  const extensionNormalize = composeNormalize(exts);
  const cleanState = extensionNormalize(normalizedState);

  const contributions = collectContentContributions(exts, cleanState);
  return {
    hasText:
      (cleanState.bodyText?.trim().length ?? 0) > 0 || contributions.text,
    hasMedia: contributions.media,
  };
}

export function getDerivedOutput(
  state: TextComposerAuthoringState,
  extensions?: TextComposerExtension[],
): TextComposerDerivedOutput {
  const flags = getContentFlags(state, extensions);
  if (!flags) {
    return { primaryChannel: "INVALID", fallbackChannel: null };
  }
  return resolveChannels(flags.hasText, flags.hasMedia);
}

export function getLinkedDerivedOutput(
  primary: TextComposerAuthoringState,
  fallback?: TextComposerAuthoringState,
  extensions?: TextComposerExtension[],
): TextComposerDerivedOutput {
  if (fallback === undefined) {
    return getDerivedOutput(primary, extensions);
  }

  const primaryFlags = getContentFlags(primary, extensions);
  const fallbackFlags = getContentFlags(fallback, extensions);
  if (!primaryFlags || !fallbackFlags) {
    return { primaryChannel: "INVALID", fallbackChannel: null };
  }

  return {
    primaryChannel: "RCS",
    fallbackChannel: fallbackFlags.hasMedia ? "MMS" : "SMS",
  };
}

export function getTransformForChannel(
  channelTransforms: TextComposerChannelTransforms,
  channel: TextComposerPrimaryChannel | TextComposerFallbackChannel,
): TextComposerChannelTransformValue {
  if (channel === "RCS") {
    return channelTransforms.rcs;
  }

  if (channel === "SMS") {
    return channelTransforms.sms;
  }

  if (channel === "MMS") {
    return channelTransforms.mms;
  }

  return null;
}
